import React, { createContext, useContext, useMemo, useState, useCallback } from 'react';

const SelectedChannelsContext = createContext(null);

export function SelectedChannelsProvider({ children }) {
  const [availableById, setAvailableById] = useState(() => new Map()); // id -> channel
  const [selectedIds, setSelectedIds] = useState(() => new Set());      // Set<number>

  // Keep existing selections, or atomically restore an explicit selection.
  // In either case, select only IDs present in the available channel list.
  const setAvailableChannels = useCallback((channels, selection) => {
    const map = new Map(channels.map(ch => [ch.id, ch]));
    setAvailableById(map);
    setSelectedIds(prev => {
      const next = new Set();
      for (const id of selection ?? prev) if (map.has(id)) next.add(id);
      return next;
    });
  }, []);

  // Replace selection with exactly these ids
  const setSelectedByIds = useCallback((ids) => {
    setSelectedIds(new Set(ids));
  }, []);

  const selectMany = useCallback((ids) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      ids.forEach(id => next.add(id));
      return next;
    });
  }, []);

  const deselectMany = useCallback((ids) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      ids.forEach(id => next.delete(id));
      return next;
    });
  }, []);

  const toggleOne = useCallback((id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const clearAll = useCallback(() => setSelectedIds(new Set()), []);

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(availableById.keys()));
  }, [availableById]);

  const availableChannels = useMemo(
    () => Array.from(availableById.values()),
    [availableById]
  );

  const selectedChannels = useMemo(() => {
    const out = [];
    for (const id of selectedIds) {
      const ch = availableById.get(id);
      if (ch) out.push(ch);
    }
    return out;
  }, [selectedIds, availableById]);

  const value = useMemo(() => ({
    // data
    availableChannels,
    selectedIds,
    selectedChannels,
    // actions
    setAvailableChannels,
    setSelectedByIds,
    selectMany,
    deselectMany,
    toggleOne,
    clearAll,
    selectAll,
    // convenience
    isSelected: (id) => selectedIds.has(id),
  }), [
    availableChannels, selectedIds, selectedChannels,
    setAvailableChannels, setSelectedByIds, selectMany, deselectMany, toggleOne, clearAll, selectAll
  ]);

  return (
    <SelectedChannelsContext.Provider value={value}>
      {children}
    </SelectedChannelsContext.Provider>
  );
}

export function useSelectedChannels() {
  const ctx = useContext(SelectedChannelsContext);
  if (!ctx) throw new Error('useSelectedChannels must be used within SelectedChannelsProvider');
  return ctx;
}
